import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Grid,
  TextField,
  CircularProgress,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { SalesStoreContext } from "../../stores/Sales/sales.provider";
import useStore from "../../helpers/useStore";
import {
  CreateServiceRechnungDto,
  CreateServicesTransactionDto,
  GiftCertificateDto,
} from "../../api-client";
import Controls from "../../atoms/Controls";
import { UserContext } from "../../stores/User/User.provider";
import { useUserAccounts } from "../../stores/User/User.hooks";
import notificationStore from "../../stores/Notification/notificationStore";
import {
  AllTransactionMethods,
  paymentStatus,
  recnungTransactionMethods,
  transactionMethods,
  transactionTypes,
} from "../Transactions/Kasse/v2/types/kasse.types";
import { TransactionsContext } from "../../stores/Transactions/Transactions.provider";

const CheckOutDialog = observer(
  ({
    patientId,
    open,
    onClose,
    isGiftCardCheckOut,
    checkoutSummary,
    selectedServices,
    dataRefreshFlag,
    setDataRefreshFlag,
  }: any) => {
    const SalesStore = useStore(SalesStoreContext);
    const UserStore = useStore(UserContext);
    const { user } = UserStore;
    const accounts = useUserAccounts();
    const { getGiftCertificateById, getGiftCertificatesByPatientId } =
      SalesStore;

    const [giftCardNumber, setGiftCardNumber] = useState("");
    const [giftCardInfo, setGiftCardInfo] = useState<{
      gcId: string;
      value: number;
      totalAmountApplied: number;
      amountToUse?: number;
    }>({
      gcId: "",
      value: 0,
      totalAmountApplied: 0,
      amountToUse: undefined,
    });

    const TransactionStore = useStore(TransactionsContext);
    const { createServiceRechnung, createServicesTransaction } =
      TransactionStore;
    const [loading, setLoading] = useState(false);

    const [validGiftCard, setValidGiftCard] = useState(false);
    const [giftCardError, setGiftCardError] = useState("");
    const [internalReason, setInternalReason] = useState("");
    const [paymentMethod, setPaymentMethod] = useState<
      transactionMethods | recnungTransactionMethods
    >(transactionMethods.CASH);
    const [accountSelection, setAccountSelection] = useState("");
    const [giftCertificates, setGiftCertificates] = useState<
      GiftCertificateDto[]
    >([]);
    const [useGiftCard, setUseGiftCard] = useState(false);
    const [accessUrl, setAccessUrl] = useState<string>();

    const handleGiftCardValidation = async (giftCardValue: string) => {
      setGiftCardError("");
      setValidGiftCard(false);
      setUseGiftCard(false);
      try {
        const response = await getGiftCertificateById(giftCardValue);
        setGiftCardInfo({
          gcId: response.giftCertificate?.id,
          value: Number(response.giftCertificate?.value) || 0,
          totalAmountApplied: Number(response.totalAmountApplied) || 0,
          amountToUse: Math.min(
            grandTotal,
            (response.giftCertificate?.value || 0) -
              (response.totalAmountApplied || 0),
          ),
        });
        setUseGiftCard(true);
        setValidGiftCard(true);
      } catch (err: any) {
        setGiftCardError(
          err.response?.data?.message ||
            err.response?.message ||
            "Fehler beim Überprüfen des Gutscheins.",
        );
      }
    };

    const grandTotal = checkoutSummary.reduce(
      (sum: number, item: any) => sum + item.total,
      0,
    );
    const isInternalIssuance = paymentMethod === transactionMethods.INTERNAL;
    const canIssueInternal =
      isGiftCardCheckOut &&
      UserStore.hasPermission("issueGiftCertificateInternal");
    const giftCardAvailable =
      giftCardInfo.value - giftCardInfo.totalAmountApplied;
    const appliedGiftAmount = useGiftCard ? giftCardInfo.amountToUse || 0 : 0;
    const finalAmount = Math.max(0, grandTotal - appliedGiftAmount);

    const transactionReason = "Payment";
    const getTransactionMethod = (type: string): AllTransactionMethods => {
      const upper = type.toUpperCase();
      if (upper in recnungTransactionMethods) {
        return recnungTransactionMethods[
          upper as keyof typeof recnungTransactionMethods
        ];
      } else {
        return transactionMethods[upper as keyof typeof transactionMethods];
      }
    };

    const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      if (!accountSelection) {
        notificationStore.showMessage("Benutzer-ID fehlt", "error");
        return;
      }
      const paymentType = getTransactionMethod(paymentMethod);

      // Dynamically handle different transaction methods
      const transactionData: CreateServicesTransactionDto = {
        // For internal issuance the reason text becomes the transaction text
        text: isInternalIssuance ? internalReason.trim() : (transactionReason as string),
        type: transactionTypes.EINNAHME as transactionTypes,
        amount: finalAmount,
        originalAmount: grandTotal,
        amountApplied: appliedGiftAmount,
        gcId: giftCardInfo.gcId ? giftCardInfo.gcId : undefined,
        method: paymentType as transactionMethods,
        accountId: accountSelection as string,
        userId: user?.id!,
        wasExported: false,
        serviceTimeSlots: !isGiftCardCheckOut ? selectedServices : undefined,
        giftCertificates: isGiftCardCheckOut ? selectedServices : undefined,
        // The server overrides tAccount/type for INTERNAL (expense account 6600)
        tAccountId: (isInternalIssuance ? 6600 : 4101) as any,
        patientId: !isGiftCardCheckOut ? patientId : undefined,
      };

      try {
        setLoading(true);
        const response = await createServicesTransaction(
          isGiftCardCheckOut,
          transactionData,
        );
        const access = response.docDto?.accessUrl;
        setAccessUrl(access);
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message;
        notificationStore.showMessage(
          `Fehler beim Erstellen der Transaktion: ${errorMessage}`,
          "error",
        );
      } finally {
        setLoading(false);
        setDataRefreshFlag(!dataRefreshFlag);
      }
    };

    const handleCreateServiceRechnung = async (
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      try {
        event.preventDefault();

        if (!user || !user.locationId) {
          notificationStore.showMessage(
            "Fehlende Kontoinformationen für den Gutschein.",
            "error",
          );
          return;
        }
        setLoading(true);
        const rechnungData: CreateServiceRechnungDto = {
          amount: finalAmount,
          originalAmount: grandTotal,
          text: "Payment",
          type: transactionTypes.EINNAHME,
          paymentStatus: paymentStatus.NOT_PAID,
          accountId: accounts?.[0]?.id!,
          serviceTimeSlots: selectedServices,
          userId: user.id,
          patientId: patientId,
          tAccountId: 4105,
          amountApplied: appliedGiftAmount,
          gcId: useGiftCard ? giftCardInfo.gcId : undefined,
        };

        const response = await createServiceRechnung(rechnungData);
        const access = response.docDto?.accessUrl;
        setAccessUrl(access);
        notificationStore.showMessage(
          "Rechnung erfolgreich erstellt!",
          "success",
        );
      } catch (error: any) {
        console.error(error);
        const errorMessage = error.response?.data?.message || error.message;
        notificationStore.showMessage(
          `Rechnung konnte nicht erstellt werden: ${errorMessage}`,
          "error",
        );
      } finally {
        setLoading(false);
        setDataRefreshFlag(!dataRefreshFlag);
      }
    };

    useEffect(() => {
      if (accessUrl) {
        window.open(accessUrl, "_blank");
        onClose();
      }
    }, [accessUrl]);

    const handleCheckout = async (
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      // Define the transaction data for checkout
      const transactionData = {
        paymentMethod,
        accountSelection,
        giftCardUsed: appliedGiftAmount,
        finalAmount,
      };

      // Check if the selected payment method is 'invoice'
      if (paymentMethod === recnungTransactionMethods.RECHNUNG) {
        await handleCreateServiceRechnung(event);
      } else {
        await handleSubmit(event);
      }

      onClose();
    };

    useEffect(() => {
      if (open && !isGiftCardCheckOut) {
        getGiftCertificatesByPatientId(patientId)
          .then((response: any) => setGiftCertificates(response))
          .catch((err) =>
            console.error("Error fetching gift certificates:", err),
          );
      }
    }, [open]);

    const handleClose = () => {
      setUseGiftCard(false);
      setGiftCardNumber("");
      setGiftCardInfo({
        gcId: "",
        value: 0,
        totalAmountApplied: 0,
        amountToUse: undefined,
      });
      setValidGiftCard(false);
      setGiftCardError("");
      setInternalReason("");
      onClose();
    };

    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Warenkorb / Zahlung</DialogTitle>

        <DialogContent>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
            {!isGiftCardCheckOut
              ? "Service-Zusammenfassung"
              : "Produktzusammenfassung"}
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Art der Behandlung</TableCell>
                <TableCell>Anzahl</TableCell>
                <TableCell>Einzelpreis</TableCell>
                <TableCell>Gesamtpreis</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {checkoutSummary.map((item: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{item.abbreviation}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>{item.unitPrice}</TableCell>
                  <TableCell>{item.total}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} align="right">
                  <strong>Gesamt:</strong>
                </TableCell>
                <TableCell>
                  <strong>{grandTotal.toFixed(2)} €</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {!isGiftCardCheckOut && (
            <>
              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Geschenkgutschein
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <Controls.Select
                    name="giftCard"
                    label="Gift Card"
                    value={giftCardNumber}
                    onChange={(e: any) => {
                      const selectedValue = e.target.value;
                      setGiftCardNumber(selectedValue);
                      if (selectedValue) {
                        handleGiftCardValidation(selectedValue);
                      }
                    }}
                    options={giftCertificates.map((gc) => ({
                      label: gc.codeLowSecurity,
                      value: gc.codeLowSecurity,
                      id: gc.id,
                    }))}
                    style={{ width: "100%" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    Betrag auf der Karte: {giftCardAvailable.toFixed(2)} €
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}

          {validGiftCard && (
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Checkbox
                  checked={useGiftCard}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setUseGiftCard(checked);
                    if (!checked) {
                      setGiftCardInfo((prev) => ({ ...prev, amountToUse: 0 }));
                      setGiftCardError("");
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Angewandter Betrag"
                  type="number"
                  value={giftCardInfo.amountToUse ?? ""}
                  onChange={(e: any) => {
                    const value = e.target.value;
                    const parsedValue = parseFloat(value);

                    const parsedValueInCents = Math.round(parsedValue * 100);
                    const availableInCents = Math.round(
                      giftCardAvailable * 100,
                    );

                    if (value === "") {
                      setGiftCardInfo((prev: any) => ({
                        ...prev,
                        amountToUse: "",
                      }));
                      setGiftCardError("");
                    } else if (
                      parsedValueInCents <= availableInCents &&
                      parsedValueInCents >= 0
                    ) {
                      setGiftCardInfo((prev) => ({
                        ...prev,
                        amountToUse: parsedValue,
                      }));
                      setGiftCardError("");
                    } else {
                      setGiftCardError(
                        "Betrag darf nicht mehr als den verfügbaren Gutscheinwert betragen.",
                      );
                    }
                  }}
                  error={Boolean(giftCardError)}
                  helperText={giftCardError}
                  fullWidth
                  disabled={!useGiftCard}
                />
              </Grid>
            </Grid>
          )}

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">Zahlungsmethode</FormLabel>
                <RadioGroup
                  value={paymentMethod}
                  onChange={(e: any) => setPaymentMethod(e.target.value)}
                >
                  <FormControlLabel
                    value={transactionMethods.CASH}
                    control={<Radio />}
                    label="Cash"
                  />
                  <FormControlLabel
                    value={transactionMethods.EC}
                    control={<Radio />}
                    label="EC Cash"
                  />
                  {!isGiftCardCheckOut && (
                    <FormControlLabel
                      value={recnungTransactionMethods.RECHNUNG}
                      control={<Radio />}
                      label="Invoice"
                    />
                  )}
                  {canIssueInternal && (
                    <FormControlLabel
                      value={transactionMethods.INTERNAL}
                      control={<Radio />}
                      label="Auf Geschäftskosten (ohne Zahlung)"
                    />
                  )}
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <Controls.Select
                name="accountId"
                label="Mandant"
                id={1}
                value={accountSelection}
                onChange={(e: any) => setAccountSelection(e.target.id)}
                options={accounts?.map((option: any) => ({
                  label: option?.name?.toString(),
                  value: option?.name?.toString(),
                  id: option?.id?.toString(),
                }))}
                style={{
                  width: "100%",
                  marginTop: "0px",
                  marginBottom: "0px",
                  fontSize: "10px",
                }}
                required
              />
            </Grid>
          </Grid>

          {isInternalIssuance && (
            <TextField
              label="Begründung"
              value={internalReason}
              onChange={(e: any) => setInternalReason(e.target.value)}
              required
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              helperText="Erforderlich – wird auf dem internen Ausgabebeleg dokumentiert (Werbeaufwand, Konto 6600)."
            />
          )}

          <Typography sx={{ mt: 2, fontWeight: "bold", textAlign: "right" }}>
            Endbetrag: {finalAmount.toFixed(2)} €
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Abbrechen
          </Button>
          <Button
            type="submit"
            disabled={
              !accountSelection ||
              giftCardError !== "" ||
              (isInternalIssuance && !internalReason.trim())
            }
            onClick={handleCheckout}
          >
            {loading ? (
              <>
                <CircularProgress size={16} color="inherit" />
                <span style={{ marginLeft: 8 }}>Bitte warten...</span>
              </>
            ) : (
              "Einreichen"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default CheckOutDialog;
